import IconFilter from 'assets/icons/filter.svg';
import Button from 'components/button-new';
import Sheet from 'components/sheet';
import { useConfig } from 'config/use-config';
import useMobile from 'hooks/common/use-mobile';
import { FilterConfig } from 'interfaces/collection-filter';
import { CollectionProduct } from 'models/collection/collection';
import { useAvailableFilterMap } from 'modules/collection/state/available-choices';
import { useRouter } from 'next/router';
import React, { useCallback, useEffect, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { Gtm } from 'tracking';
import FilterGroup from '../../filter-group';
import ResultCounter from '../../result-counter';
interface FilterMobileProps {
  appliedFilter: string[];
  filterConfig: FilterConfig | undefined;
  activeAllergyFilter: string[];
  resetFilter: () => void;
  toggleFilter: (value: string) => void;
  collectionProducts: CollectionProduct[];
  visibleProducts: CollectionProduct[];
}

const FilterMobile: React.FC<FilterMobileProps> = ({
  appliedFilter,
  filterConfig,
  activeAllergyFilter,
  resetFilter,
  toggleFilter,
  collectionProducts,
  visibleProducts,
}) => {
  const { commonCollections } = useConfig();
  const { asPath } = useRouter();
  const isCatWetFoodPage = asPath.includes(commonCollections.catsWetFood);

  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const availableFilterMap = useAvailableFilterMap(collectionProducts, filterConfig);

  const numberOfActiveFilters =
    // Add fake filters to the number of active filters
    // @see https://petsdeli.atlassian.net/browse/PT-5632
    appliedFilter.length + activeAllergyFilter.length;

  // In case there are not other filters than the hero filter, we don't want to show the filter button
  const showFilterButton = (filterConfig: FilterConfig): boolean => {
    return (
      filterConfig &&
      !Array.isArray(filterConfig) &&
      typeof filterConfig === 'object' &&
      (Object.keys(filterConfig).length > 1 || !('hero-filter' in filterConfig))
    );
  };

  const findGroupKey = useCallback(
    (t: string) => {
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const map = Object.entries(availableFilterMap).find(([_, conf]) =>
        (conf.tags as string[]).includes(t)
      );
      return map ? map[0] : String(undefined);
    },
    [availableFilterMap]
  );

  /**
   * Filter out 'line' and 'sorts' filters from tracking when on cat wet food page
   * since CatGeneralFoodPageFilters handles its own tracking for those
   */
  const shouldTrackFilter = useCallback(
    (filter: string): boolean => {
      if (!isCatWetFoodPage) return true;

      const groupKey = findGroupKey(filter);
      return groupKey !== 'line' && groupKey !== 'sorts';
    },
    [isCatWetFoodPage, findGroupKey]
  );

  const isMobile = useMobile();
  const [prevAppliedFilter, setPrevAppliedFilter] = useState<string[]>([]);

  useEffect(() => {
    if (isMobile && !isMenuOpen) {
      const removedFilters = prevAppliedFilter.filter((filter) => !appliedFilter.includes(filter));

      const addedFilters = appliedFilter.filter((filter) => !prevAppliedFilter.includes(filter));

      // Only track filters that should be tracked (exclude 'line' and 'sorts' on cat wet food page)
      removedFilters.filter(shouldTrackFilter).forEach((filter) =>
        Gtm.track.unselectElement({
          element: ['Filter', findGroupKey(filter), filter],
        })
      );

      addedFilters.filter(shouldTrackFilter).forEach((filter) =>
        Gtm.track.selectElement({
          element: ['Filter', findGroupKey(filter), filter],
        })
      );

      setPrevAppliedFilter(appliedFilter);
    }
  }, [appliedFilter, isMobile, prevAppliedFilter, isMenuOpen, findGroupKey, shouldTrackFilter]);

  const handleClose = (): void => {
    setIsMenuOpen(false);
    const isActive = numberOfActiveFilters > 0;
    const eventElement = isActive ? 'Apply' : 'Close';
    Gtm.track.selectElement({
      element: ['Filter', eventElement],
    });
  };

  if (!filterConfig) return null;

  return (
    <>
      <div
        className="text-text-secondary flex items-center justify-between py-1.5 text-sm"
        data-test="collection-filter"
      >
        <ResultCounter productCount={visibleProducts.length} />

        {filterConfig && showFilterButton(filterConfig) && (
          <>
            <button
              className="flex items-center gap-2 leading-5"
              onClick={() => {
                setIsMenuOpen((prevState: boolean) => !prevState);
                Gtm.track.selectElement({ element: ['Filter', 'Toggle'] });
              }}
            >
              <FormattedMessage id="cdp:filter:label" />

              <span className="relative inline-block">
                <IconFilter className="h-[14px] w-[14px]" />
                {numberOfActiveFilters > 0 && (
                  <div className="absolute top-0 -right-1 -mt-1 mr-0.5 h-1.5 w-1.5 rounded-full bg-red-600" />
                )}
              </span>
            </button>
          </>
        )}
      </div>

      <Sheet isOpen={isMenuOpen} onClose={handleClose} disableScrollLocking={isMenuOpen}>
        {filterConfig && (
          <div className="mb-[60px] h-full flex-auto overflow-y-auto px-4">
            {filterConfig && (
              <FilterGroup
                filterConfig={filterConfig}
                appliedFilter={appliedFilter}
                toggleFilter={toggleFilter}
                collectionProducts={collectionProducts}
                visibleProducts={visibleProducts}
              />
            )}
          </div>
        )}

        <div className="bg-other-white absolute bottom-0 flex w-full gap-5 px-4 py-3 shadow-sm">
          <Button
            size="base"
            layout="outline"
            styleDisabled={numberOfActiveFilters === 0}
            disabled={numberOfActiveFilters === 0}
            onClick={() => {
              Gtm.track.selectElement({
                element: ['Filter', 'Unselect All'],
              });
              resetFilter();
            }}
          >
            <FormattedMessage id="cdp:resetfilters" />
          </Button>

          <Button size="base" onClick={handleClose}>
            {numberOfActiveFilters ? (
              <>
                {visibleProducts.length + ' '}
                <FormattedMessage
                  id={
                    visibleProducts.length === 1
                      ? 'cdp:filter:showproduct'
                      : 'cdp:filter:showproducts'
                  }
                />
              </>
            ) : (
              <FormattedMessage id="cdp:filter:close" />
            )}
          </Button>
        </div>
      </Sheet>
    </>
  );
};

export default FilterMobile;
